import React, { useEffect, useState } from 'react';
import { MenuItem } from '../../../components/Menu';
import embedIcon from '../../../icons/editor/embed.svg';
import newTabIcon from '../../../icons/editor/newTab.svg';
import EDITOR_INTEGRATION_MENTIONS from '../../mentions/EditorIntegrationMentions';
import { useQuill } from '../../QuillContext';
import type { EmbedBlot } from '../../types';
import getEditorMentionUrl from '../../utils/getEditorMentionUrl';
import matchMention from '../../utils/matchMention';
import replaceBlotWith from '../../utils/replaceBlotWith';
import DisplayAsLinkMenuItem from '../DisplayAsLinkMenuItem';
import { editLinkIcon } from '../editorIcons';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarOverflowMenuButton from './buttons/ToolbarOverflowMenuButton';
import LinkEditor from './LinkEditor';

interface MentionToolbarProps {
  blot: EmbedBlot;
  initialEditingTarget?: 'link' | 'text';
}

const MentionToolbar = ({
  blot,
  initialEditingTarget,
}: MentionToolbarProps) => {
  const { quill, readOnly } = useQuill();
  const [link, setLink] = useState<string | null>(null);
  const [initialText, setInitialText] = useState<string | null>(null);
  const [textPlaceholder, setTextPlaceholder] = useState<string | null>(null);
  const [editingTarget, setEditingTarget] = useState<null | 'link' | 'text'>(
    initialEditingTarget || null,
  );
  const [canBeRenderedAsEmbed, setCanBeRenderedAsEmbed] = useState(false);

  useEffect(() => {
    const { id, params, type } = blot.statics.value(blot.domNode);
    const formats = blot.statics.formats(blot.domNode) || {};
    setLink(
      getEditorMentionUrl({ id, type, params, content: formats.content }),
    );

    if (
      quill?.scroll.query('embed') &&
      (type === 'post' ||
        EDITOR_INTEGRATION_MENTIONS[type]?.modes.includes('embed'))
    ) {
      setCanBeRenderedAsEmbed(true);
    }
  }, [quill, blot]);

  if (editingTarget) {
    return (
      <LinkEditor
        blot={blot}
        initialLink={link || ''}
        initialText={initialText || ''}
        textPlaceholder={textPlaceholder || ''}
        editingTarget={editingTarget}
        onEditingTargetChange={setEditingTarget}
        onSave={(value) => {
          if (!quill) return;

          if (editingTarget === 'link') {
            const mention = matchMention(value);
            if (mention) {
              replaceBlotWith(quill, blot, { mention: mention.value });
            } else {
              replaceBlotWith(quill, blot, value, { link: value });
            }
          } else if (editingTarget === 'text') {
            if (
              !value ||
              value === blot.statics.formats(blot.domNode)?.content
            ) {
              blot.format('mention-text', null);
            } else {
              blot.format('mention-text', value);
            }
          }
        }}
      />
    );
  }

  return (
    <>
      {!readOnly && (
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={editLinkIcon}
            tooltip="Edit"
            onClick={() => {
              const formats = blot.statics.formats(blot.domNode) || {};
              setInitialText(formats['mention-text'] || formats.content);
              setTextPlaceholder(formats.content);
              setEditingTarget('link');
            }}
            testId="mentionToolbarEditButton"
          />
        </ToolbarButtonGroup>
      )}
      <ToolbarCommentButtonGroup blot={blot} />
      {link && (
        <ToolbarButtonGroup>
          <ToolbarOverflowMenuButton testId="mentionToolbarMenuButton">
            {(onClose) => (
              <>
                <MenuItem
                  icon={newTabIcon}
                  to={link}
                  target="_blank"
                  onClick={onClose}
                >
                  Open in new tab
                </MenuItem>
                {quill && canBeRenderedAsEmbed && (
                  <MenuItem
                    icon={embedIcon}
                    onClick={() => {
                      const { id, type } = blot.statics.value(blot.domNode);
                      replaceBlotWith(quill, blot, { embed: { type, id } });
                    }}
                    testId="displayAsEmbedMenuItem"
                  >
                    Display as embed
                  </MenuItem>
                )}
                {!readOnly && quill && (
                  <DisplayAsLinkMenuItem
                    quill={quill}
                    blot={blot}
                    link={link}
                  />
                )}
              </>
            )}
          </ToolbarOverflowMenuButton>
        </ToolbarButtonGroup>
      )}
    </>
  );
};

export default MentionToolbar;
